import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { ContentPlanRootState as S } from '../../../elements/contentPlan/store';
import { Functions } from '../../../types/types';
import { ElementAction } from '../../../elements/elementAction';
import { Metadata } from '../../../types/Metadata';
import { updateMetadataFormState } from '../metadataFormReducer';
import { ViewType } from '../../../enums/ViewType';
import { NodeType } from '../../../enums/NodeType';
import { RelationshipFieldType } from '../../../enums/RelationshipFieldType';
import { ContentType } from '../../../enums/ContentType';
import { Op } from '../../../enums/Op';
import { View } from '../../../types/View';
import { getIsChildSelect } from './relationshipField';
import { reloadData } from '../metadataFormActions';

const removeProjectDerivativesInContent =
  (
    data: Partial<Metadata>,
    parent: Partial<Metadata>,
    isChildSelect?: boolean
  ): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { removeProjectDerivativesRelationship } = getAPI();
    await removeProjectDerivativesRelationship(
      [data?.props?.itemId],
      parent?.props?.itemId,
      isChildSelect
    );
    // Updates the form state differently if child or parent
    if (isChildSelect) {
      parent.props.projectBy = parent.props?.projectBy.filter(
        c => c?.props?.refId != data?.props?.refId
      );
      dispatch(updateMetadataFormState({ data: { ...parent } }));
    } else {
      data.props.projectOf = [];
      dispatch(updateMetadataFormState({ data: { ...data } }));
    }
  };

const createChildRelationshipInContent =
  (
    type: RelationshipFieldType,
    data: Partial<Metadata>,
    children: Metadata[]
  ): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { createUniOfRelationship, addProjectDerivativesRelationship } =
      getAPI();
    const itemId = data?.props?.itemId;
    if (children == null) {
      return;
    }
    const childItemIds = children?.map(c => c?.props?.itemId);
    switch (type) {
      case RelationshipFieldType.ProjectDerivatives: {
        await addProjectDerivativesRelationship(childItemIds, itemId, true);
        data.props.projectBy = [...(data.props.projectBy || []), ...children];
      }
    }
    dispatch(updateMetadataFormState({ data: { ...data } }));
  };

const createRelationshipInContent =
  (
    type: RelationshipFieldType,
    data: Partial<Metadata>,
    parent: Metadata
  ): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { createUniOfRelationship, addProjectDerivativesRelationship } =
      getAPI();
    const parentItemId = parent?.props?.itemId;
    const itemId = data?.props?.itemId;
    switch (type) {
      case RelationshipFieldType.UNI: {
        await createUniOfRelationship(itemId, parentItemId);
        data.props.uniOf = [parent];
        break;
      }
      case RelationshipFieldType.ProjectDerivatives: {
        await addProjectDerivativesRelationship([itemId], parentItemId, false);
        data.props.projectOf = [parent];
      }
    }
    dispatch(updateMetadataFormState({ data: { ...data } }));
  };

const getSearchConditions = (
  data: Partial<Metadata>,
  t: RelationshipFieldType
) => {
  const defaultFilters = [];
  if (data?.props?.refId) {
    // exclude self...
    defaultFilters.push({
      field: 'Item.title',
      conditions: [{ [Op.notRefIds]: [data.props.refId] }]
    });
  }
  if (data?.props?.itemType) {
    defaultFilters.push({
      field: 'Item.itemType',
      conditions: [{ [Op.is]: [data.props.itemType] }]
    });
  }
  switch (t) {
    case RelationshipFieldType.UNI:
      const uniFilters: View.FiltersField[] = [
        ...defaultFilters,
        {
          field: 'Item.uniBy',
          conditions: [
            {
              isEmpty: true
            }
          ]
        }
      ];
      if (data?.props && data.props.contentType?.length) {
        const sameParentChildContentTypes = [
          ContentType.MAGIC_QUADRANT,
          ContentType.CRITICAL_CAPABILITIES,
          ContentType.INNOVATION_PROFILE,
          ContentType.HYPE_CYCLE,
          ContentType.Radar_Profile,
          ContentType.Emerging_Tech_Impact_Radar
        ];
        for (const ctId of sameParentChildContentTypes) {
          if (data.props.contentType[0].props?.id == ctId) {
            uniFilters.push({
              field: 'Item.contentType',
              conditions: [
                {
                  [Op.refIds]: ['dtyp:' + ctId]
                }
              ]
            });
            break;
          }
        }
      }
      return uniFilters;
    case RelationshipFieldType.ProjectDerivatives:
      if (getIsChildSelect(data, t)) {
        return [
          ...defaultFilters,
          {
            field: 'Item.contentType',
            conditions: [
              {
                notRefIds: ['dtyp:314']
              }
            ]
          },
          {
            field: 'Item.projectOf',
            conditions: [
              {
                isEmpty: true
              }
            ]
          }
        ];
      } else {
        return [
          ...defaultFilters,
          {
            field: 'Item.contentType',
            conditions: [
              {
                refIds: ['dtyp:314']
              }
            ]
          },
          {
            field: 'Item.uniBy',
            conditions: [
              {
                isEmpty: true
              }
            ]
          }
        ];
      }
  }
  return defaultFilters;
};

const searchItems =
  (
    data: Partial<Metadata>,
    type: RelationshipFieldType,
    query: string,
    page: number
  ): ElementAction<Functions, S> =>
  async (dispatch, getState, { getAPI }) => {
    const { searchData } = getAPI();
    const queryCondition = !isNaN(Number.parseInt(query))
      ? {
          field: 'Item.itemId',
          conditions: [
            {
              equals: query
            }
          ]
        }
      : {
          field: 'Item.title',
          conditions: [
            {
              contains: query
            }
          ]
        };
    const searchConditions = getSearchConditions(data, type);

    const queryFilters = [queryCondition, ...searchConditions];

    if (!getIsChildSelect(data, type)) {
      // Planning Child and Content Child
      queryFilters.push({
        field: 'Item.itemId',
        conditions: [
          {
            isNotEmpty: true
          }
        ]
      });
    }

    const spayload = {
      view: {
        props: {
          type: ViewType.Tabular
        },
        card: {
          props: {
            data: NodeType.Item,
            fields: [{ field: 'series' }]
          }
        },
        dataSchema: undefined
      },
      filters: queryFilters,
      limit: (page + 1) * 25
    };

    const r = await searchData(spayload as any);
    if (r?.data) {
      return {
        data: r.data,
        pages: Math.floor(r.info.count / 25)
      };
    }
    return null;
  };

const createSeries =
  (
    refId: string,
    series: Partial<Metadata>,
    items: Metadata[]
  ): ElementAction<Functions, S> =>
  async (dispatchEvent, getState, { getAPI }) => {
    const { updateData } = getAPI();
    const payload = {
      data: { ...series, props: { ...series?.props, items: items } }
    };
    const updateDataResponse = await updateData(payload);
    dispatchEvent(reloadData(refId, [NodeType.Item], true));
    return updateDataResponse;
  };

export const useActions = () => {
  const dispatch: Dispatch<any> = useDispatch();
  return {
    searchItems: async (
      data: Partial<Metadata>,
      type: RelationshipFieldType,
      query: string,
      limit: number
    ) => await dispatch(searchItems(data, type, query, limit)),
    removeProjectDerivativesInContent: async (
      data: Partial<Metadata>,
      parent: Partial<Metadata>,
      isChildSelect?: boolean
    ) =>
      await dispatch(
        removeProjectDerivativesInContent(data, parent, isChildSelect)
      ),
    createRelationshipInContent: async (
      type: RelationshipFieldType,
      data: Partial<Metadata>,
      parent: Metadata
    ) => await dispatch(createRelationshipInContent(type, data, parent)),
    createChildRelationshipInContent: async (
      type: RelationshipFieldType,
      data: Partial<Metadata>,
      children: Metadata[]
    ) => await dispatch(createChildRelationshipInContent(type, data, children)),
    createSeries: async (
      refId: string,
      series: Partial<Metadata>,
      items: Metadata[]
    ) => await dispatch(createSeries(refId, series, items))
  };
};
